import { useState, useEffect } from 'react';
import Header from '../components/header/Header';
import SideMenu from '../components/navigation/SideMenu';
import AppNavbar from '../components/navigation/AppNavbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { StyledDataGrid } from '../components/customizations/dataGrid';
import { LineChart } from '@mui/x-charts/LineChart';
import { useAuth } from '../authentication/AuthContext';
import axiosInstance from '../authentication/AuthContext';

// Extract a human-readable prompt from an event's data payload.
function getPromptFromData(data) {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (typeof data !== 'object') return String(data);

    if (data['query']) {
        return '"' + data['query'] + '"';
    }
    return null;
}

export default function Analytics() {
    const { accessToken } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedType, setSelectedType] = useState('all');

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axiosInstance.get('/analytics/');
                let raw = response.data;
                if (Array.isArray(raw)) {
                    setEvents(raw);
                } else if (raw?.results) {
                    setEvents(raw.results);
                } else if (raw?.data) {
                    setEvents(Array.isArray(raw.data) ? raw.data : []);
                } else {
                    setEvents([]);
                }
            } catch (err) {
                console.error('Failed to fetch analytics:', err);
                setError(
                    err.response?.data?.detail || 'Failed to load analytics'
                );
                setEvents([]);
            } finally {
                setLoading(false);
            }
        };
        if (accessToken) fetchAnalytics();
    }, [accessToken]);

    // Chart data: sorted by time, using per-event latency and token usage (llm_end)
    const chartData = events
        .filter((e) => e.timestamp)
        .map((e) => {
            const ts = new Date(e.timestamp).getTime();
            return {
                id: e.id ?? ts,
                timestamp: ts,
                label: new Date(e.timestamp).toLocaleString(),
                latency: e.latency != null ? Number(e.latency) : null,
                token_usage:
                    e.event_type === 'llm_end' &&
                    e.data?.token_usage?.total_tokens != null
                        ? Number(e.data.token_usage.total_tokens)
                        : null,
            };
        })
        .sort((a, b) => a.timestamp - b.timestamp);

    const hasChartData = chartData.length > 0;
    const latencyValues = chartData.map((d) => d.latency);
    const tokenValues = chartData.map((d) => d.token_usage);
    const xLabels = chartData.map((d) => d.label);

    const columns = [
        {
            field: 'event_type',
            headerName: 'Event Type',
            width: 140,
        },
        {
            field: 'run_id',
            headerName: 'Run ID',
            width: 130,
            renderCell: (params) =>
                params.value ? `...${String(params.value).slice(0, 8)}` : '—',
        },
        {
            field: 'latency',
            headerName: 'Latency (s)',
            width: 110,
            renderCell: (params) =>
                params.value != null ? Number(params.value).toFixed(2) : '—',
        },
        {
            field: 'token_usage',
            headerName: 'Token Usage',
            width: 120,
            renderCell: (params) => {
                const e = params.row;
                const n =
                    e.event_type === 'llm_end' &&
                    e.data?.token_usage?.total_tokens != null
                        ? e.data.token_usage.total_tokens
                        : e.total_tokens;
                return n != null ? n : '—';
            },
        },
        {
            field: 'user',
            headerName: 'User',
            width: 130,
            renderCell: (params) => {
                const u = params.value;
                if (u == null) return '—';
                if (typeof u === 'number') return `#${u}`;
                if (typeof u === 'string') return u;
                return (
                    u.username ?? u.email ?? (u.id != null ? `#${u.id}` : '—')
                );
            },
        },
        {
            field: 'prompt',
            headerName: 'Prompt',
            flex: 1,
            minWidth: 220,
            renderCell: (params) => {
                const content = getPromptFromData(
                    params.row.data ?? params.value
                );
                if (!content) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    );
                }
                return (
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                        {content}
                    </Typography>
                );
            },
        },
        {
            field: 'timestamp',
            headerName: 'Timestamp',
            width: 160,
            renderCell: (params) =>
                params.value ? new Date(params.value).toLocaleString() : '—',
        },
    ];

    const rows = events.map((e, i) => ({
        ...e,
        id: e.id ?? `row-${i}`,
    }));

    const typeCounts = events.reduce((acc, e) => {
        const t = e.event_type || 'unknown';
        acc[t] = (acc[t] || 0) + 1;
        return acc;
    }, {});
    const eventTypes = Object.keys(typeCounts).sort();

    const filteredRows =
        selectedType === 'all'
            ? rows
            : rows.filter((r) => (r.event_type || 'unknown') === selectedType);

    return (
        <Box sx={{ display: 'flex' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    bgcolor: 'background.default',
                }}
            >
                <Header />
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        maxWidth: '100%',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                    }}
                >
                    {loading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {!loading && !error && (
                        <>
                            {hasChartData && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 3,
                                        mb: 3,
                                    }}
                                >
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography
                                            variant="subtitle1"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Token usage over time
                                        </Typography>
                                        <LineChart
                                            xAxis={[
                                                {
                                                    scaleType: 'point',
                                                    data: xLabels,
                                                    tickLabelStyle: {
                                                        fontSize: 10,
                                                    },
                                                },
                                            ]}
                                            series={[
                                                {
                                                    data: tokenValues,
                                                    label: 'Tokens',
                                                    showMark: true,
                                                    connectNulls: true,
                                                },
                                            ]}
                                            height={280}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                bottom: 60,
                                                left: 50,
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                    </Paper>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography
                                            variant="subtitle1"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            Latency (s) over time
                                        </Typography>
                                        <LineChart
                                            xAxis={[
                                                {
                                                    scaleType: 'point',
                                                    data: xLabels,
                                                    tickLabelStyle: {
                                                        fontSize: 10,
                                                    },
                                                },
                                            ]}
                                            series={[
                                                {
                                                    data: latencyValues,
                                                    label: 'Latency (s)',
                                                    showMark: true,
                                                    connectNulls: true,
                                                },
                                            ]}
                                            height={280}
                                            margin={{
                                                top: 20,
                                                right: 30,
                                                bottom: 60,
                                                left: 50,
                                            }}
                                            sx={{ width: '100%' }}
                                        />
                                    </Paper>
                                </Box>
                            )}
                            <Paper
                                variant="outlined"
                                sx={{ width: '100%', overflow: 'hidden' }}
                            >
                                {eventTypes.length > 0 && (
                                    <Tabs
                                        value={selectedType}
                                        onChange={(_, value) =>
                                            setSelectedType(value)
                                        }
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{
                                            px: 2,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Tab
                                            label={`All (${events.length})`}
                                            value="all"
                                        />
                                        {eventTypes.map((type) => (
                                            <Tab
                                                key={type}
                                                label={`${type} (${typeCounts[type]})`}
                                                value={type}
                                            />
                                        ))}
                                    </Tabs>
                                )}
                                <Typography
                                    variant="subtitle1"
                                    color="text.secondary"
                                    sx={{ px: 2, pt: 2, pb: 1 }}
                                >
                                    Events ({filteredRows.length})
                                </Typography>
                                <StyledDataGrid
                                    rows={filteredRows}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                />
                            </Paper>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
